import { Box, Link, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mapDispatchToProps, mapStateToProps } from './SignIn.container';

import { FormikCheckbox } from '/b2b/common/components/FormControls/Checkbox';
import { FormikTextField } from '/b2b/common/components/FormControls/TextField';
import FormikValidationForm from '/b2b/common/components/organisms/FormikValidationForm';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import UnauthenticatedPaths from '/b2b/routing/UnauthenticatedRoutes/UnauthenticatedRoutes.paths';
import { connect } from 'react-redux';
import { signInSchema } from './SignIn.schema';

// eslint-disable-next-line no-use-before-define
export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
export function SignIn(props) {
    const { email, onSignIn, rememberMe, ssoEnabled } = props;
    const isMounted = useRef(false);
    const formikBag = useRef();
    const [useCognito, setUseCognito] = useState(!ssoEnabled);
    const submitText = useCognito ? 'common.signIn' : 'common.next';

    const handleSignIn = params => {
        return onSignIn({ ...params, useCognito }).then(context => {
            isMounted.current && setUseCognito(!!context);
            return { useCognito: !!context };
        });
    };

    const values = useMemo(() => {
        return {
            rememberMe: formikBag.current?.values?.['rememberMe'] ?? rememberMe,
            useCognito: useCognito === null ? !!email : useCognito,
            username: email || '',
        };
    }, [email, rememberMe, useCognito]);

    useEffect(() => {
        isMounted.current = true;
        return () => (isMounted.current = false);
    });

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            alignItems="stretch"
            justifyContent="center"
            sx={{ mx: 'auto', px: 2, maxWidth: 700, width: '100%' }}
        >
            <FormikValidationForm
                formikRef={formikBag}
                initialValues={values}
                validationSchema={signInSchema}
                onSubmit={handleSignIn}
                title="common.signIn"
                submitText={submitText}
                enableReinitialize
                validateOnMount
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    flexGrow={1}
                    gap={0.5}
                    alignItems="center"
                    mt={4}
                    mb={1}
                    sx={theme => (theme.breakpoints.down('lg') ? { justifyContent: 'center' } : {})}
                >
                    <FormikTextField
                        name="username"
                        label="common.email"
                        autoComplete="username"
                        fullWidth
                        disabled={useCognito}
                        mb={1}
                    />
                    <FormikTextField
                        allowShowHide
                        name="password"
                        label="common.fields.password.label"
                        type="password"
                        autoComplete="current-password"
                        fullWidth
                        depends={[['useCognito', true]]}
                    />
                    <FormikCheckbox name="rememberMe" label="common.rememberMe" fullWidth />
                </Box>
            </FormikValidationForm>
            {useCognito && (
                <Box mt={5}>
                    <Typography variant="body1" mb={2}>
                        <Link
                            fontWeight="bold"
                            component={RouterLink}
                            to={UnauthenticatedPaths.FORGOT_PASSWORD}
                        >
                            <Locale path="password.forgot" />
                        </Link>
                    </Typography>
                    <Typography variant="body1">
                        <Locale path="auth.shouldUseDifferentAccount" />{' '}
                        <Link
                            fontWeight="bold"
                            component={RouterLink}
                            to={UnauthenticatedPaths.SIGN_IN}
                            onClick={() => setUseCognito(false)}
                        >
                            <Locale path="auth.useDifferentAccount" />
                        </Link>
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

SignIn.propTypes = {
    className: PropTypes.string,
    email: PropTypes.string,
    isLoading: PropTypes.bool,
    onSignIn: PropTypes.func.isRequired,
    rememberMe: PropTypes.bool,
    ssoEnabled: PropTypes.bool,
};

SignIn.defaultProps = {
    ssoEnabled: true,
    rememberMe: false,
};
